/**
 * Знакомство с приложением.
 *
 * Четыре экрана, по одной мысли на каждом, и «Пропустить» на всех. Это не
 * обучение: человеку, который только что завёл профиль, нужно понять, почему
 * здесь календарь, а не список дел, и где лежит то, за чем он пришёл. Всё
 * остальное он узнает, пользуясь.
 *
 * Последний экран — единственный, где что-то предлагают сделать: разрешить
 * уведомления и настроить режим дня. Без первого не придёт ни одно
 * напоминание, без второго день считается по умолчанию — и оба этих места
 * человек находит сам в последнюю очередь.
 */

import {
  Bell,
  CalendarDays,
  Check,
  HeartPulse,
  ListTodo,
  Moon,
  Repeat,
  ShoppingBasket,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';

import { ReminderPermission } from '@/features/reminders/ui/ReminderPermission';
import { cn } from '@/shared/lib/cn';
import { Button, Sheet } from '@/shared/ui';

interface OnboardingSheetProps {
  open: boolean;
  /** Пройдено или пропущено — разницы нет: второй раз само не появится. */
  onDone: () => void;
}

interface Step {
  title: string;
  description: string;
  body?: ReactNode;
}

/** Строка раздела: значок, название и одна фраза о том, что внутри. */
function Row({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand-soft-text">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-medium">{title}</span>
        <span className="mt-0.5 block text-[13px] leading-relaxed text-muted">{text}</span>
      </span>
    </li>
  );
}

function useSteps(close: () => void): Step[] {
  const navigate = useNavigate();

  const go = (route: string) => {
    close();
    void navigate(route);
  };

  return [
    {
      title: 'Начните с режима дня',
      description:
        'Заполните сон и приёмы пищи — это каркас дня. Дела приложение разложит в свободное время между ними, и сразу станет видно, сколько его на самом деле: «двенадцать задач на день» не превратятся в вечный долг.',
      body: (
        <ul className="space-y-3">
          <Row
            icon={<Moon size={18} aria-hidden />}
            title="Сначала сон и еда"
            text="Во сколько ложитесь, встаёте и едите — это и есть режим дня. Настраивается один раз, на последнем шаге знакомства."
          />
          <Row
            icon={<CalendarDays size={18} aria-hidden />}
            title="Календарь показывает день целиком"
            text="Видно, что помещается, а что нет, — до того, как день начался."
          />
        </ul>
      ),
    },
    {
      title: 'Где что лежит',
      description: 'Пять разделов внизу, и каждый отвечает на свой вопрос.',
      body: (
        <ul className="space-y-3">
          <Row
            icon={<CalendarDays size={18} aria-hidden />}
            title="Календарь"
            text="День по часам: сон, еда, дела, встречи и приёмы лекарств."
          />
          <Row
            icon={<ListTodo size={18} aria-hidden />}
            title="Задачи"
            text="Что нужно сделать. Невыполненное вчера само доезжает до сегодня."
          />
          <Row
            icon={<Repeat size={18} aria-hidden />}
            title="Привычки"
            text="Регулярное и цели, ради которых это регулярное существует."
          />
          <Row
            icon={<HeartPulse size={18} aria-hidden />}
            title="Здоровье"
            text="Замеры, таблетница с курсами и аптечка — что лежит дома и до какой даты годно."
          />
          <Row
            icon={<ShoppingBasket size={18} aria-hidden />}
            title="Ещё"
            text="Покупки, фильмы и книги, настройки профиля."
          />
        </ul>
      ),
    },
    {
      title: 'Отмечать — одно касание',
      description:
        'Всё отмечается там, где вы это видите: задача — кружком в списке, приём лекарства — кнопкой в ленте дня, привычка — одним нажатием на карточке. Ошиблись — рядом появится «Отменить».',
      body: (
        <ul className="space-y-3">
          <Row
            icon={<Check size={18} aria-hidden />}
            title="Сделано"
            text="Отметка ставится за секунду и сразу видна в дне и в прогрессе."
          />
          <Row
            icon={<CalendarDays size={18} aria-hidden />}
            title="Перенести"
            text="В календаре плашку можно подхватить удержанием и перетащить на другое время."
          />
        </ul>
      ),
    },
    {
      title: 'Чтобы всё работало',
      description:
        'Два места, которые стоит открыть сразу. Данные живут на этом телефоне: настройте резервную копию в профиле, чтобы они пережили его.',
      body: (
        <div className="space-y-3">
          <ReminderPermission />
          <Button
            variant="secondary"
            block
            iconLeft={<Bell size={17} aria-hidden />}
            onClick={() => go('/profile')}
          >
            Напоминания и резервная копия
          </Button>
          <Button
            variant="secondary"
            block
            iconLeft={<Moon size={17} aria-hidden />}
            onClick={() => go('/profile/routine')}
          >
            Настроить режим дня
          </Button>
        </div>
      ),
    },
  ];
}

export function OnboardingSheet({ open, onDone }: OnboardingSheetProps) {
  const [index, setIndex] = useState(0);
  const steps = useSteps(onDone);

  /*
    Каждое открытие начинается сначала.

    Шторка остаётся в памяти закрытой, и без сброса «Пройти ещё раз»
    открывало знакомство там, где его закрыли в прошлый раз — например, на
    последнем экране с кнопками. Со стороны это выглядит как сбой: человек
    просил пройти заново, а ему показали конец.
  */
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setIndex(0);
  }

  const step = steps[index]!;
  const last = index === steps.length - 1;

  return (
    <Sheet
      open={open}
      onClose={onDone}
      title={step.title}
      description={step.description}
      height="full"
      footer={
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-1.5" aria-hidden>
            {steps.map((item, position) => (
              <span
                key={item.title}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-200',
                  position === index ? 'w-5 bg-brand' : 'w-1.5 bg-border',
                )}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {/* «Пропустить» стоит на каждом экране: знакомство, из которого
                нельзя выйти, читают по диагонали и закрывают с раздражением. */}
            <Button variant="ghost" size="lg" block onClick={onDone}>
              {last ? 'Закрыть' : 'Пропустить'}
            </Button>
            {!last && (
              <Button size="lg" block onClick={() => setIndex((value) => value + 1)}>
                Дальше
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step.body}
    </Sheet>
  );
}
